/** Types and fetch wrappers for the shadow console's API
 * (services/console_api/server.py). Same-origin only -- the console is
 * only ever served by that same process, locally or behind the ALB in
 * the test account (decision 0025). */

export type Severity = "HIGH" | "MEDIUM" | "LOW";
export type SeverityStyle = "exception" | "review" | "neutral";

export interface FindingItem {
  client_slug: string;
  client_name: string;
  profile_missing: boolean;
  bank_account_id: string;
  bank_account_name: string;
  finding_type: string;
  severity: Severity;
  severity_label: string;
  severity_style: SeverityStyle;
  reasoning: string;
  next_step: string; // one suggested next step for this finding type (views.NEXT_STEPS)
  days_until_next_deadline: number | null;
}

export interface ClientRow {
  client_slug: string;
  client_name: string;
  profile_missing: boolean;
  refreshed_at: string | null;
  bank_account_name: string | null;
  days_since_last_transaction: number | null;
  finding_type: string | null;
  severity: Severity | null;
  severity_label: string | null;
  severity_style: SeverityStyle | null;
  days_until_next_deadline: number | null;
  next_deadline_date: string | null; // ISO date, e.g. "2026-10-21"
  // Entity-level facts for the one-client summary strip.
  lodgement_pattern: string | null; // e.g. "Quarterly BAS · Monthly IAS"
  next_deadline: NextDeadline | null; // from the lodgement pattern, as of today
  xero_status: "connected" | "not_checked" | "reconnect_required";
  xero_status_label: string;
  xero_status_style: "exception" | null;
}

export interface DeadlineRow {
  client_slug: string;
  client_name: string;
  label: string; // e.g. "September IAS"
  due_date: string; // ISO date
  days_left: number;
  status: "at_risk" | "due_soon" | "upcoming";
  status_label: string;
  status_style: "exception" | null; // the Exception pill for "At risk" only
}

export interface DeadlinesPayload {
  deadlines: DeadlineRow[];
  clients: Array<{ client_slug: string; client_name: string; lodgement_pattern: string | null }>;
}

export interface NeedsYou {
  at_risk: FindingItem[];
  needs_review: FindingItem[];
  waiting_on_others: FindingItem[];
}

export interface ViewPayload {
  needs_you: NeedsYou;
  clients: ClientRow[];
  signed_in?: boolean; // true only when the server has identity on
  // Present only when identity is enabled and the caller has a real
  // session (security review 2026-09-28's SR-02) -- returned here, not
  // just at login, so a page reload (which loses everything this module
  // holds in memory) can recover it without signing in again.
  csrf_token?: string;
}

export interface GapBaseline {
  median_days: number;
  p95_days: number;
  threshold_days: number;
}

export interface VolumeComparison {
  count_this_period: number;
  trailing_average: number;
  same_period_last_year: number;
  is_historically_regular: boolean;
}

export interface DetailFinding {
  finding_type: string;
  severity: Severity;
  severity_label: string;
  severity_style: SeverityStyle;
  reasoning: string;
  next_step: string; // one suggested next step for this finding type (views.NEXT_STEPS)
  days_until_next_deadline: number | null;
}

export interface NextDeadline {
  label: string; // e.g. "September IAS"
  due_date: string; // ISO date, e.g. "2026-10-21"
  days_until: number;
}

export interface DetailPayload {
  client_slug: string;
  client_name: string;
  profile_missing: boolean;
  bank_account_id: string;
  bank_account_name: string;
  finding: DetailFinding | null;
  next_deadline: NextDeadline | null;
  signals: {
    days_since_last_transaction: number | null;
    gap_baseline: GapBaseline | null;
    recurring_payee_count: number;
    missing_recurring_payee_count: number;
    volume: VolumeComparison | null;
    unreconciled_count: number;
  };
}

/** Every error body the server sends: a machine-readable `error` and,
 * for anything a person should read, a `message` already written for
 * them (what happened and what to do). */
export interface ErrorBody {
  error?: string;
  message?: string;
  [key: string]: unknown;
}

// The current session's CSRF token, held in memory only -- never in
// localStorage/sessionStorage, matching the server's own "never a
// cookie" choice (services/console_api/server.py's _verify_csrf docstring:
// a cross-origin page can't read a JSON response body, so keeping it
// out of any storage a same-origin XSS bug could also read is one less
// place it needs to be defended). Picked up from every response that
// carries one -- /api/view on every load (so a page reload recovers it),
// every sign-in step that starts a session, and refreshes -- including
// error bodies (a 409 reconnect_required still carries the view).
let csrfToken: string | undefined;

/** Every state-changing request needs Content-Type: application/json
 * (SR-02) even with no body -- the server checks this before touching
 * session state -- plus the current CSRF token, once one exists. The
 * browser adds Origin itself. */
function stateChangingHeaders(): Record<string, string> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (csrfToken) headers["X-CSRF-Token"] = csrfToken;
  return headers;
}

function rememberCsrfToken(payload: unknown): void {
  const token = (payload as { csrf_token?: unknown } | null)?.csrf_token;
  if (typeof token === "string" && token) csrfToken = token;
}

/** Forgets the token on sign-out or when the server says the session is
 * gone, so a stale one is never sent for the next person's session. */
/** The CSRF token for the recorder engine's member routes (memory only). */
export function currentCsrfToken(): string | undefined {
  return csrfToken;
}

/** E's finding 6: the terms step after a reload has no other page data, so its 403 terms_required carries this session's CSRF
 * token (A) for Accept and Sign out. */
export function rememberCsrfFrom(body: unknown): void {
  rememberCsrfToken(body);
}

export function forgetCsrfToken(): void {
  csrfToken = undefined;
  // The device-recorder check buffers audio chunks in IndexedDB while it uploads (decision 0051);
  // nothing of it survives a sign-out. A no-op where the check never ran.
  try {
    indexedDB.deleteDatabase("pk-device-check");
  } catch {
    /* no IndexedDB here */
  }
}

async function parse<T>(res: Response): Promise<T> {
  let body: unknown = {};
  try {
    body = await res.json();
  } catch {
    // An empty or non-JSON body (e.g. the ALB's own error page) -- the
    // status alone still says what happened.
  }
  rememberCsrfToken(body);
  if (!res.ok) {
    const err = body as ErrorBody;
    throw new ApiError(err.message ?? `Request failed (${res.status}).`, res.status, err.error ?? null, err);
  }
  return body as T;
}

async function getJson<T>(path: string): Promise<T> {
  return parse<T>(await fetch(path, { credentials: "same-origin" }));
}

async function postJson<T>(path: string, body?: object): Promise<T> {
  return parse<T>(
    await fetch(path, {
      method: "POST",
      credentials: "same-origin",
      headers: stateChangingHeaders(),
      body: JSON.stringify(body ?? {}),
    })
  );
}

/** `?clients=a,b` for the client filter, or nothing for all clients.
 * The server narrows it to the caller's own scope regardless. */
function clientsQuery(clients: readonly string[] = []): string {
  return clients.length === 0 ? "" : `?clients=${clients.map(encodeURIComponent).join(",")}`;
}

export async function fetchView(clients: readonly string[] = []): Promise<ViewPayload> {
  const payload = await getJson<ViewPayload>(`/api/view${clientsQuery(clients)}`);
  rememberCsrfToken(payload);
  return payload;
}

export function fetchDeadlines(clients: readonly string[] = []): Promise<DeadlinesPayload> {
  return getJson<DeadlinesPayload>(`/api/deadlines${clientsQuery(clients)}`);
}

/** Triggers exactly one Xero call for this client and returns the
 * refreshed view (filtered like the page). Call once per client per
 * explicit user action -- never in a loop the user didn't ask for. */
export function refreshClient(clientSlug: string, clients: readonly string[] = []): Promise<ViewPayload> {
  return postJson<ViewPayload>(`/api/refresh/${encodeURIComponent(clientSlug)}${clientsQuery(clients)}`);
}

export function fetchDetail(clientSlug: string, bankAccountId: string): Promise<DetailPayload> {
  return getJson<DetailPayload>(
    `/api/detail/${encodeURIComponent(clientSlug)}/${encodeURIComponent(bankAccountId)}`
  );
}

// ---- Closed beta: who's asking, Xero connections, feedback ----

/** What this caller may do (GET /api/me). Advisory only -- it decides
 * which rail items to show; every route enforces its own access. */
export interface Me {
  identity_enabled: boolean;
  can_manage_xero_connections: boolean;
  can_send_feedback: boolean;
  can_view_onboarding: boolean; // account_admin or super_admin
  // The client filter's options: every entity this person may see.
  entities: Array<{ client_slug: string; client_name: string }>;
  email?: string | null; // the account's email from Cognito, for the rail (display only)
  meetings_enabled?: boolean; // cdk.json meetingsEnabled for this environment (false in prod)
  chime_enabled?: boolean; // the Chime room (in-person meetings, guest links); off in prod even with Meetings on
  // The recording cap (CAP-008.WP11), from services/meetings/recorder.py; only when the recorder is on.
  recording_limits?: { limit_minutes: number; warn_minutes: number };
  // Console v0.5 (session A's contract v1): absent = today's console. Typed in full in v05/routes.ts (V05Me).
  console_v05?: boolean;
}

export interface XeroConnectionRow {
  client_slug: string;
  client_name: string;
  organisation_name: string | null; // null when unreachable
  connection_id: string | null; // Xero's connection id, never the tenant id; null when unreachable
  status: "connected" | "unreachable";
  status_label: string; // "Connected" / "Unreachable" -- connection state, not a display status
  status_style: "exception" | null; // unreachable = the brand's Exception status; connected = no status
  message: string | null; // what happened and what to do, when unreachable
  can_reconnect: boolean; // Xero refused the saved sign-in or no longer lists it (decision 0047)
  checked_at: string; // ISO instant Xero was actually asked (status is reused for up to 60 s)
}

export interface FeedbackInput {
  screen: string;
  doing: string;
  expected: string;
  details: string;
}

/** A failed request, carrying the server's own user-facing message
 * (what happened and what to do) when it sent one. `code` is the
 * server's machine-readable `error`; `body` is the whole error response,
 * for the few callers that need more than the message (the terms
 * version to accept, or the view that comes back with a
 * reconnect_required refresh). */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string | null,
    readonly body: ErrorBody = {}
  ) {
    super(message);
  }
}

/** What to do when a request answers 403 terms_required in the middle of a session (e.g. a new Privacy Policy to accept). Today's
 * console handles it in its own error path; the new console sets this to reload, so start-up shows the acceptance screen. */
let onTermsRequired: (() => void) | null = null;
export function setOnTermsRequired(handler: (() => void) | null): void {
  onTermsRequired = handler;
}

async function send<T>(
  method: "GET" | "POST",
  path: string,
  fallback: string,
  body?: unknown,
  guest = false
): Promise<T> {
  if (method === "POST" && !csrfToken && !guest) {
    // State-changing routes need this session's CSRF token (SR-02), which
    // the server hands back on /api/view. Normally already read on page
    // load; if not, read it now rather than send a request bound to fail.
    await fetchView().catch(() => undefined);
  }
  const attempt = async (): Promise<{ res: Response; payload: ErrorBody }> => {
    let res: Response;
    try {
      res = await fetch(path, {
        method,
        // POST: Content-Type application/json + X-CSRF-Token, always --
        // even the server's Origin check relies on this being a real fetch
        // from the console's own page.
        headers: method === "POST" ? stateChangingHeaders() : undefined,
        body: body === undefined ? undefined : JSON.stringify(body),
      });
    } catch {
      throw new ApiError(`${fallback} Check your connection and try again.`, 0, null);
    }
    let payload: ErrorBody = {};
    try {
      payload = (await res.json()) as ErrorBody;
    } catch {
      // Non-JSON body -- fall through to the generic message below.
    }
    return { res, payload };
  };

  let { res, payload } = await attempt();
  if (method === "POST" && !guest && res.status === 403 && payload.error === "csrf_check_failed") {
    // The server refused before doing anything, so one retry with a
    // freshly read token is safe (e.g. the token in memory belonged to an
    // earlier session in this tab).
    await fetchView().catch(() => undefined);
    ({ res, payload } = await attempt());
    if (res.status === 403 && payload.error === "csrf_check_failed") {
      throw new ApiError(
        `${fallback} Preekie couldn't confirm the request came from this page. Reload the page and try again.`,
        res.status,
        payload.error ?? null,
        payload
      );
    }
  }
  if (!res.ok) {
    if (res.status === 403 && payload.error === "terms_required" && !guest) onTermsRequired?.();
    throw new ApiError(
      payload.message ?? `${fallback} Try again in a few minutes.`,
      res.status,
      payload.error ?? null,
      payload
    );
  }
  return payload as T;
}

export function fetchMe(): Promise<Me> {
  return send<Me>("GET", "/api/me", "Preekie couldn't check your access.");
}

/** Connection status per organisation. The server reuses a status
 * checked in the last 60 seconds; `fresh` ("Check now") asks Xero
 * straight away. */
export function fetchXeroConnections(fresh = false): Promise<{ connections: XeroConnectionRow[] }> {
  return send(
    "GET",
    fresh ? "/api/xero/connections?fresh=1" : "/api/xero/connections",
    "Preekie couldn't load your Xero connections."
  );
}

export function disconnectXeroConnection(
  clientSlug: string,
  connectionId: string
): Promise<{ disconnected: true; message: string }> {
  return send(
    "POST",
    `/api/xero/connections/${encodeURIComponent(clientSlug)}/disconnect`,
    "Preekie couldn't disconnect this organisation, so it's still connected.",
    { connection_id: connectionId }
  );
}

/** A one-time Xero consent link (decision 0047), valid for 10 minutes. */
export function startXeroReconnect(clientSlug: string): Promise<{ authorization_url: string; expires_at: string }> {
  return send(
    "POST",
    `/api/xero/connections/${encodeURIComponent(clientSlug)}/reconnect`,
    "Preekie couldn't start reconnecting this organisation. Nothing changed.",
    {}
  );
}

/** The same request helper for the new console's routes (src/v05/*-api.ts): CSRF on POST unless `guest`. */
export function apiRequest<T>(method: "GET" | "POST", path: string, fallback: string, body?: unknown, guest = false): Promise<T> {
  return send<T>(method, path, fallback, body, guest);
}

export function sendFeedback(input: FeedbackInput): Promise<{ received: true; message: string }> {
  return send("POST", "/api/feedback", "Your feedback wasn't sent. Your text is still here.", input);
}

// ---- Sign-in (decision 0019's routes; first sign-in added in 0025) ----

export type SignInStep =
  | { challenge: "SOFTWARE_TOKEN_MFA"; session: string }
  | { challenge: "NEW_PASSWORD_REQUIRED"; session: string }
  | {
      challenge: "MFA_SETUP";
      session: string;
      secret_code: string; // base32 key, for "Can't scan it?"
      issuer: string; // "Preekie" in prod, "Preekie (test)" in test
      otpauth_uri: string; // built on the server (qr.py) -- never assembled here
      qr_svg: string; // QR of otpauth_uri, inline SVG made on our server (segno)
    };

export interface SessionStarted {
  terms_required?: boolean;
  // CAP-009: the super admin's own sign-in ends with {is_super_admin, csrf_token} (A, contracts s.13).
  is_super_admin?: boolean;
}

export type SignInResult = SignInStep | SessionStarted;

export function isSessionStarted(result: SignInResult): result is SessionStarted {
  return "terms_required" in result || "is_super_admin" in result;
}

// Where the sign-in steps post: the console's own login, or (CAP-009) the super admin's separate one, which has the
// same steps and bodies under /api/admin/login (A, contracts s.13).
let loginBase = "/api/login";

export function useLoginBase(base: "/api/login" | "/api/admin/login"): void {
  loginBase = base;
}

/** Reads a CSRF token from a GET that returns one (the super admin's /api/admin/me after a reload). */
export async function refreshCsrfFrom(path: string): Promise<void> {
  await getJson<unknown>(path).catch(() => undefined);
}

export function signIn(email: string, password: string): Promise<SignInResult> {
  return postJson<SignInResult>(loginBase, { email, password });
}

export function submitMfaCode(email: string, session: string, code: string): Promise<SignInResult> {
  return postJson<SignInResult>(`${loginBase}/mfa`, { email, session, code });
}

export function submitNewPassword(email: string, session: string, newPassword: string): Promise<SignInResult> {
  return postJson<SignInResult>(`${loginBase}/new-password`, { email, session, new_password: newPassword });
}

export function submitMfaSetup(email: string, session: string, code: string): Promise<SignInResult> {
  return postJson<SignInResult>(`${loginBase}/mfa-setup`, { email, session, code });
}

export function acceptTerms(documentVersion: string, documentHash: string): Promise<{ accepted: boolean }> {
  return postJson("/api/accept-terms", { document_version: documentVersion, document_hash: documentHash });
}

export function signOut(): Promise<{ signed_out: boolean }> {
  return postJson("/api/logout");
}

// ---- Preekie Meetings (docs/meetings-api.md; only where meetingsEnabled) ----

export interface MeetingRow {
  meeting_id: string;
  title: string;
  client_slug: string;
  client_name: string;
  scheduled_start: string; // ISO with offset; render in the viewer's zone
  length_minutes: number;
  invited_count: number;
  mode: "online" | "in_person";
  status: string; // Requested | Awaiting review | Approved | Exception
  processing?: boolean; // to_review only
  exception_reason?: string | null; // to_review, Exception only
}

export interface MeetingsList {
  upcoming: MeetingRow[];
  to_review: MeetingRow[];
  approved?: MeetingRow[]; // only with ?include=approved
}

export interface GuestInvite {
  invite_id: string;
  email: string;
  join_url: string; // relative to the console origin: /m/<id>/<token>
  expires_at: string;
}

export interface CreatedMeeting extends MeetingRow {
  host_link: string; // /m/<id>
  guest_invites: GuestInvite[];
}

export type RecordingStatus = "not_started" | "recording" | "concatenating" | "stopped";

export interface MeetingDetail extends MeetingRow {
  is_host: boolean;
  recording_status: RecordingStatus;
  recording_started_at: string | null;
  // external_user_id: the attendee's opaque Chime id, to name video tiles (requested from session A).
  participants: Array<{ display_name: string; kind: "member" | "guest"; external_user_id?: string }>;
  lobby_count?: number; // the host's own response only
}

/** What the Chime browser SDK's MeetingSessionConfiguration takes,
 * exactly as AWS returns it. */
export interface JoinInfo {
  meeting_id: string;
  Meeting: Record<string, unknown>;
  Attendee: Record<string, unknown>;
}

export interface LobbyGuest {
  invite_id: string;
  display_name: string;
  consent_given_at: string;
}

export interface GuestLobbyInfo {
  firm: string;
  title: string;
  scheduled_start: string;
  notice: string; // the one-line recording notice, a server constant
  status: "invited" | "waiting" | "admitted";
  // Once admitted only (#152): who's in the room, to name video tiles.
  participants?: Array<{ display_name: string; kind: "member" | "guest"; external_user_id?: string }>;
  recording_status?: RecordingStatus; // once admitted (requested from session A); unknown until then
}

export interface MeetingAction {
  text: string;
  owner: string | null;
  due_date: string | null; // YYYY-MM-DD
  due_text?: string | null; // the words used ("by this Friday"), when there's no exact date
  // What to show (#190): the words plus the worked-out date, "tomorrow (13 Nov)";
  // a date the words already carry isn't repeated; a date alone is "3 Dec".
  due_display?: string | null;
  owner_is_host?: boolean; // CAP-009 (A s.18): the owner is the recorder's microphone ("Host")
}

/** One item as checked against the transcript (#190/#192; docs/meetings-api.md,
 * "How the notes are checked"). Same order as the plain lists. */
export interface ReviewItem {
  text: string;
  evidence?: string | null; // the words from the transcript that support it
  verified: boolean | null; // null: a person wrote or changed it
  unverified_reason?: string | null;
  date_inferred?: Array<{ resolved: string; said: string }> | null;
  edited_by_person?: boolean;
  owner?: string | null; // actions
  due_date?: string | null;
  due_text?: string | null;
  due_display?: string | null;
}

export interface FigureNotInNotes {
  said: string; // the clause
  values: string[];
  turn: number; // index into the transcript's turns (0-based)
  speaker: string | null;
}

export interface MeetingSummary {
  status: string;
  processing: boolean;
  summary_ready: boolean;
  details: {
    title: string;
    client_name: string;
    scheduled_start: string;
    length_minutes: number;
    host_name: string;
    recording_pauses?: Array<{ from: string; to: string | null }>; // CAP-009 (A s.21): the recording's pauses
  };
  decisions: string[];
  actions: MeetingAction[];
  figures_and_deadlines: string[];
  open_questions: string[];
  transcript_excerpt: string[];
  transcript_url: string | null;
  audio_deleted_at: string | null;
  edited: boolean;
  exception_reason?: string | null;
  // Review detail (#190/#192): absent on notes prepared before them.
  items?: {
    decisions: ReviewItem[];
    actions: ReviewItem[];
    figures_and_deadlines: ReviewItem[];
    open_questions: ReviewItem[];
  };
  unverified_count?: number;
  date_inferred_count?: number;
  figures_not_in_notes?: FigureNotInNotes[];
  approved_by_name?: string | null;
  handed_over_to_name?: string | null;
  // CAP-009 (A s.18): the excerpt as items, so the recorder's name can replace "Host" without parsing text.
  transcript_excerpt_items?: Array<{ speaker: string | null; is_host: boolean; text: string }>;
}

const m = (id: string) => `/api/meetings/${encodeURIComponent(id)}`;

export function fetchMeetings(clients: readonly string[] = [], includeApproved = false): Promise<MeetingsList> {
  const q = clientsQuery(clients);
  const path = `/api/meetings${q}${includeApproved ? `${q ? "&" : "?"}include=approved` : ""}`;
  return send("GET", path, "Preekie couldn't load your meetings.");
}

export function createMeeting(payload: Record<string, unknown>): Promise<CreatedMeeting> {
  return send("POST", "/api/meetings", "The meeting wasn't created.", payload);
}

export function fetchMeeting(id: string): Promise<MeetingDetail> {
  return send("GET", m(id), "Preekie couldn't load this meeting.");
}

export function joinMeeting(id: string): Promise<JoinInfo> {
  return send("POST", `${m(id)}/join`, "Preekie couldn't join you to the meeting.");
}

export function fetchLobby(id: string): Promise<{ waiting: LobbyGuest[] }> {
  return send("GET", `${m(id)}/lobby`, "Preekie couldn't check the lobby.");
}

export function admitGuest(id: string, inviteId: string): Promise<{ invite_id: string; status: "admitted" }> {
  return send("POST", `${m(id)}/admit`, "The guest wasn't let in.", { invite_id: inviteId });
}

export function startRecording(
  id: string,
  everyoneAgreed?: boolean
): Promise<{ recording_status: RecordingStatus; recording_started_at: string | null }> {
  const body = everyoneAgreed === undefined ? {} : { host_declared_everyone_agreed: everyoneAgreed };
  return send("POST", `${m(id)}/recording/start`, "Recording didn't start.", body);
}

export function stopRecording(id: string): Promise<{ recording_status: RecordingStatus }> {
  return send("POST", `${m(id)}/recording/stop`, "Recording didn't stop.");
}

export function endMeeting(id: string): Promise<{ ended: true; recording_status: RecordingStatus }> {
  return send("POST", `${m(id)}/end`, "The meeting didn't end.");
}

export function fetchSummary(id: string): Promise<MeetingSummary> {
  return send("GET", `${m(id)}/summary`, "Preekie couldn't load the notes.");
}

// Guests have no session: the token in the path is their whole
// authorisation, so these never send (or fetch) a CSRF token.

export function fetchGuestLobby(id: string, token: string): Promise<GuestLobbyInfo> {
  return send("GET", `${m(id)}/guest/${encodeURIComponent(token)}`, "Preekie couldn't open this meeting.", undefined, true);
}

export function enterGuestLobby(id: string, token: string, displayName: string): Promise<{ status: "waiting" }> {
  return send(
    "POST",
    `${m(id)}/guest/${encodeURIComponent(token)}/lobby`,
    "You weren't added to the lobby.",
    { consent_given: true, display_name: displayName },
    true
  );
}

/** Rejects with ApiError code "not_admitted_yet" (403) until the host admits them. */
export function guestJoin(id: string, token: string): Promise<JoinInfo> {
  return send("POST", `${m(id)}/guest/${encodeURIComponent(token)}/join`, "Preekie couldn't join you.", undefined, true);
}

// Review (point 4 of the Meetings plan; host or account admin only). These
// routes arrive with the summary work -- until then they answer 404 and the
// screen says so.

export interface SummaryEdit {
  decisions: string[];
  actions: MeetingAction[];
  figures_and_deadlines: string[];
  open_questions: string[];
}

export function editSummary(id: string, edit: SummaryEdit): Promise<MeetingSummary> {
  return send("POST", `${m(id)}/summary/edit`, "Your changes weren't saved.", edit);
}

export function approveSummary(id: string, handOverTo: string, note: string): Promise<{ status: "Approved" }> {
  return send("POST", `${m(id)}/summary/approve`, "The notes weren't approved.", { hand_over_to: handOverTo, note });
}

export function fetchHandoverOptions(id: string): Promise<{ members: Array<{ user_id: string; display_name: string }> }> {
  return send("GET", `${m(id)}/handover-options`, "Preekie couldn't load who to hand over to.");
}

export function fetchTranscript(
  id: string
): Promise<{
  turns: Array<{ speaker: string; start: number; text: string; is_host?: boolean }>;
  host_name?: string | null;
  pauses?: Array<{ before_turn: number; from: string; to: string | null }>; // CAP-009 (A s.21)
}> {
  return send("GET", `${m(id)}/transcript`, "Preekie couldn't load the transcript.");
}

// ---- Device recorder (decision 0051 v1; docs/recorder-api.md) ----

/** Exactly the POST /api/recorder response (the engine's own type). */
export type RecorderStarted = import("./recorder/types.js").Started;

/** Closes a recording that never got going (the engine failed straight
 * after Start), so it can't block the next Start for 72 hours. */
export function cancelRecorder(recordingId: string): Promise<{ status: string }> {
  return send("POST", `/api/recorder/${encodeURIComponent(recordingId)}/cancel`, "The recording wasn't closed.", { confirm: true });
}

/** The consent step: the server writes the consent record, then
 * recording_started, then issues the token. 409 or 5xx: try again; never
 * upload without a token. */
export function startRecorder(body: Record<string, unknown>): Promise<RecorderStarted> {
  return send("POST", "/api/recorder", "The recording didn't start.", body);
}
